import { NavLink, Outlet } from 'react-router-dom';
import {
  BookOpen,
  FerrisWheel,
  FlaskConical,
  History,
  Leaf,
  NotebookPen,
  Settings as SettingsIcon,
  type LucideIcon,
} from 'lucide-react';
import { useHealth } from '../hooks/useHealth';
import { SamsungInternetBanner } from './SamsungInternetBanner';
import { PRIMARY_NAV_ITEMS } from './primaryNav';
import { AppHeader } from './AppHeader';
import { legacyServerToolsEnabled } from '../lib/runtimeFeatures';
import { PwaUpdateNotice } from './PwaUpdateNotice';

const NAV_ICONS: Record<string, LucideIcon> = {
  '/': BookOpen,
  '/calculator': FlaskConical,
  '/spin': FerrisWheel,
  '/history': History,
  '/rituals': NotebookPen,
  '/settings': SettingsIcon,
};

/**
 * Mobile-first shell with one shared Conscious Ritual brand header.
 * Primary destinations include Spin the Wheel; Dev is not in the mobile nav.
 */
export function Layout(): JSX.Element {
  const legacyToolsEnabled = legacyServerToolsEnabled();
  const { status, label, detail } = useHealth(20000, legacyToolsEnabled);

  const online = status === 'online';
  const checking = status === 'checking';

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <AppHeader />
      <PwaUpdateNotice />

      {legacyToolsEnabled && !online && !checking && (
        <div className="health-banner" role="status" aria-live="polite">
          <strong>{label}</strong>
          <span className="hint">{detail}</span>
          <span className="hint">Attempting reconnect…</span>
        </div>
      )}

      <SamsungInternetBanner />

      <main id="main" className="app-main">
        <Outlet />
      </main>

      <nav className="bottom-nav" aria-label="Primary">
        {PRIMARY_NAV_ITEMS.map((item) => {
          const Icon = NAV_ICONS[item.to] ?? Leaf;
          return (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <span className="nav-icon-wrap" aria-hidden="true">
                <Icon className="nav-icon" />
              </span>
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
